import React from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Divider, Skeleton, TextField, Stack, Button, Tooltip, CircularProgress } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { getCustomers, getInvoices } from '../../../utils/api';

export default function BusinessCustomers() {
  const [allCustomers, setAllCustomers] = React.useState([]);
  const [invoices, setInvoices] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [filterName, setFilterName] = React.useState('');
  const [filterPhone, setFilterPhone] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [paginationInfo, setPaginationInfo] = React.useState({
    hasMore: false,
    totalPages: 1,
    totalItems: 0
  });

  React.useEffect(() => {
    const loadInvoices = async () => {
      try {
        const invRes = await getInvoices();
        setInvoices(invRes.data?.results || []);
      } catch (error) {
        console.error('Error loading invoices:', error);
        setInvoices([]);
      }
    };
    loadInvoices();
  }, []);

  // Load customers with pagination
  React.useEffect(() => {
    const loadCustomers = async () => {
      if (page === 1) {
        setLoading(true);
        setAllCustomers([]); // Reset customers on first page
      } else {
        setLoadingMore(true);
      }

      try {
        const custRes = await getCustomers(page, 20);
        const customerResults = custRes.data?.results || [];
        
        // Update pagination info
        setPaginationInfo({
          hasMore: (custRes.data?.has_next ?? (customerResults.length === 20)) || false,
          totalPages: custRes.data?.total_pages || 1,
          totalItems: custRes.data?.total_items || 0
        });
        
        if (page === 1) {
          setAllCustomers(customerResults);
        } else {
          // Append new customers to existing ones
          setAllCustomers(prev => [...prev, ...customerResults]);
        }
        
      } catch (error) {
        console.error('Error loading customers:', error);
        if (page === 1) {
          setAllCustomers([]);
        }
        setPaginationInfo({ hasMore: false, totalPages: 1, totalItems: 0 });
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };

    loadCustomers();
  }, [page]);

  // Reset pagination when filters change
  React.useEffect(() => {
    setPage(1);
  }, [filterName, filterPhone]);

  // Process and filter customer data
  const processedCustomers = React.useMemo(() => {
    const shopId = localStorage.getItem('shopId');
    
    // Filter invoices for this shop
    const shopInvoices = invoices.filter(inv => 
      inv.shopId === shopId || inv.company_id === shopId
    );
    
    // Create customer data with invoice information
    const customerData = allCustomers.map(customer => {
      // Find all possible phone number fields
      const phoneNumber = customer.phone || customer.phoneNumber || customer.mobile;
      
      // Find invoices for this customer
      const customerInvoices = shopInvoices.filter(inv => 
        inv.customerNumber === phoneNumber
      );
      
      // Calculate total spent and categories
      const totalSpent = customerInvoices.reduce((sum, inv) => 
        sum + (Number(inv.amount) || 0), 0
      );
      
      const categories = Array.from(new Set(
        customerInvoices.map(inv => inv.category).filter(Boolean)
      ));
      
      return {
        name: customer.name || phoneNumber,
        phone: phoneNumber,
        totalSpent,
        categories,
        hasInvoices: customerInvoices.length > 0
      };
    });

    return customerData.filter(cust => {
      const nameMatch = !filterName || 
        (cust.name && cust.name.toLowerCase().includes(filterName.toLowerCase()));
      const phoneMatch = !filterPhone || 
        (cust.phone && cust.phone.includes(filterPhone));
      return nameMatch && phoneMatch;
    });
  }, [allCustomers, invoices, filterName, filterPhone]);

  const loadMore = () => {
    if (paginationInfo.hasMore && !loadingMore) {
      setPage(prevPage => prevPage + 1);
    }
  };

  if (loading && page === 1) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
        <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', borderRadius: 6, boxShadow: 0, border: '1px solid', borderColor: 'divider', mx: 'auto', background: 'background.paper' }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <PeopleAltIcon sx={{ color: 'text.secondary', fontSize: 32 }} />
            <Typography variant="h4" fontWeight={700} color="text.primary">
              Customers
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="text.secondary" mb={2}>
            All customers who have made purchases at your business.
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'top', justifyContent: 'center', py: { xs: 2, md: 6 }, px: { xs: 1, md: 0 } }}>
      <Paper sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, width: '100%', borderRadius: 6, boxShadow: 0, border: '1px solid', borderColor: 'divider', mx: 'auto', background: 'background.paper' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PeopleAltIcon sx={{ color: 'text.secondary', fontSize: 32 }} />
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Customers
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="text.secondary" mb={2}>
          All customers who have made purchases at your business. 
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {processedCustomers.length === 0 && !loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6}>
            <PeopleAltIcon sx={{ color: '#e0e0e0', fontSize: 64, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" fontWeight={600} mb={1}>
              No customers found yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Customers will app/Appear here once they make a purchase at your business.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            {/* Filters */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
              <TextField
                label="Search Name"
                size="small"
                value={filterName}
                onChange={e => setFilterName(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Search Mobile"
                size="small"
                value={filterPhone}
                onChange={e => setFilterPhone(e.target.value)}
                sx={{ minWidth: 140 }}
              />

              <Box sx={{ flexGrow: 1 }} />
              <Box>
                <Tooltip title="Clear all filters">
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        setFilterName('');
                        setFilterPhone('');
                      }}
                      disabled={
                        !filterName && !filterPhone
                      }
                      sx={{ ml: { xs: 0, sm: 2 }, mt: { xs: 1, sm: 0 } }}
                    >
                      Clear Filters
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Name</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Phone</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Total Spent</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper', color: 'text.primary', fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>Categories</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processedCustomers.map((cust, idx) => (
                    <TableRow key={`${cust.phone}-${idx}`} hover>
                      <TableCell>{cust.name}</TableCell>
                      <TableCell>{cust.phone}</TableCell>
                      <TableCell>
                        {cust.totalSpent > 0 ? `₹${cust.totalSpent.toLocaleString()}` : ''}
                      </TableCell>
                      <TableCell>
                        {cust.categories.length > 0 ? (
                          cust.categories.map((cat, catIdx) => (
                            <Chip 
                              key={`${cat}-${catIdx}`} 
                              label={cat} 
                              size="small" 
                              sx={{ mr: 0.5, mb: 0.5 }} 
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {paginationInfo.hasMore && (
              <Box display="flex" justifyContent="center" mt={3} gap={2}>
                <Button 
                  variant="contained" 
                  onClick={loadMore}
                  disabled={loadingMore}
                  size="large"
                  startIcon={loadingMore ? <CircularProgress color="inherit" size={18} /> : null}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
            
            {!paginationInfo.hasMore && allCustomers.length > 0 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  Showing all {allCustomers.length} customers
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}
